/**
 * Configuration — Content Control — catalogues, compatibility, sample rules.
 * Owns content checking and live responses. Configuration — Flag owns Flag
 * requirements, qualification, Severity and case destination.
 * Detection service capabilities remain NOT CONFIRMED (deferred).
 */

export const CONTENT_CONTROL_ROUTE = '/admin/configuration/content-control';

/** Section dependencies that remain outstanding. */
export const CCT_DEFERRED_DEFINITIONS = [
  {
    id: 'detection_capabilities',
    label: 'Internal and approved-service capabilities',
    status: 'NOT CONFIRMED',
    detail: 'Supported Methods, languages and formats remain deferred for service/integration work.',
  },
  {
    id: 'evidence_exception_mapping',
    label: 'Support Ticket and Appeal evidence exception source/form mapping',
    status: 'NOT DEFINED',
    detail: 'Exact mapping for not rejecting reported or challenged material in evidence.',
  },
];

export const CCT_READINESS_BANNER =
  'NOT YET READY — detection capabilities and evidence-exception source mapping remain outstanding. Catalogue Rules can be viewed; Approved Integration and Combined require confirmed services.';

/** Approved external services are not confirmed for Save. Internal catalogue remains editable for UI samples. */
export const CCT_APPROVED_SERVICES_CONFIRMED = false;

export const MODULE_OPTIONS = [
  { value: 'customers', label: 'Customers' },
  { value: 'business_profiles', label: 'Business Profiles' },
  { value: 'posts', label: 'Posts' },
  { value: 'messages', label: 'Messages' },
  { value: 'quotes', label: 'Quotes' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'support_tickets', label: 'Support Tickets' },
  { value: 'appeals', label: 'Appeals' },
];

export const CONTENT_AREA_OPTIONS = [
  { value: 'first_name', label: 'First Name', module: 'customers', form: 'text' },
  { value: 'last_name', label: 'Last Name', module: 'customers', form: 'text' },
  { value: 'business_name', label: 'Business Name', module: 'business_profiles', form: 'text' },
  { value: 'profile_headline', label: 'Profile Headline', module: 'business_profiles', form: 'text' },
  { value: 'profile_description', label: 'Profile Description', module: 'business_profiles', form: 'text' },
  { value: 'logo', label: 'Logo', module: 'business_profiles', form: 'image' },
  { value: 'header_banner', label: 'Header Banner', module: 'business_profiles', form: 'image' },
  { value: 'profile_image', label: 'Profile Image', module: 'business_profiles', form: 'image' },
  { value: 'portfolio_image', label: 'Portfolio Image', module: 'business_profiles', form: 'image' },
  { value: 'structured_contact_details', label: 'Structured Contact Details', module: 'business_profiles', form: 'text' },
  { value: 'allowed_external_links', label: 'Allowed External Links', module: 'business_profiles', form: 'text' },
  { value: 'post_title', label: 'Post Title', module: 'posts', form: 'text' },
  { value: 'post_description', label: 'Post Description', module: 'posts', form: 'text' },
  { value: 'post_image', label: 'Post Image', module: 'posts', form: 'image' },
  { value: 'message_thread_text', label: 'Message Thread Text', module: 'messages', form: 'text' },
  { value: 'message_thread_image', label: 'Message Thread Image', module: 'messages', form: 'image' },
  { value: 'message_thread_attachment', label: 'Message Thread Attachment', module: 'messages', form: 'attachment' },
  { value: 'quote_request_content', label: 'Quote Request Content', module: 'quotes', form: 'text' },
  { value: 'quote_request_images', label: 'Quote Request Images', module: 'quotes', form: 'image' },
  { value: 'quote_request_attachments', label: 'Quote Request Attachments', module: 'quotes', form: 'attachment' },
  { value: 'quote_thread_text', label: 'Quote Thread Text', module: 'quotes', form: 'text' },
  { value: 'quote_thread_image', label: 'Quote Thread Image', module: 'quotes', form: 'image' },
  { value: 'quote_thread_attachment', label: 'Quote Thread Attachment', module: 'quotes', form: 'attachment' },
  { value: 'review_content', label: 'Review Content', module: 'reviews', form: 'text' },
  { value: 'review_images', label: 'Review Images', module: 'reviews', form: 'image' },
  { value: 'business_reply', label: 'Business Reply', module: 'reviews', form: 'text' },
  { value: 'ticket_subject', label: 'Subject', module: 'support_tickets', form: 'text' },
  { value: 'ticket_description', label: 'Message', module: 'support_tickets', form: 'text' },
  { value: 'ticket_evidence_attachment', label: 'Ticket Evidence Attachment', module: 'support_tickets', form: 'attachment' },
  { value: 'appeal_message_text', label: 'Appeal Message Text', module: 'appeals', form: 'text' },
  { value: 'appeal_message_attachment', label: 'Appeal Message Attachment', module: 'appeals', form: 'attachment' },
];

export const CONTENT_CONTEXT_OPTIONS = [
  { value: 'account_setup', label: 'Account Setup' },
  { value: 'public_content', label: 'Public Content' },
  { value: 'private_communication', label: 'Private Communication' },
  { value: 'private_evidence', label: 'Private Evidence' },
  { value: 'structured_business_contact', label: 'Structured Business Contact' },
];

export const TRIGGER_OPTIONS = [
  { value: 'next', label: 'Next' },
  { value: 'signup_submit', label: 'Signup Submit' },
  { value: 'save_or_update', label: 'Save or Update' },
  { value: 'upload', label: 'Upload' },
  { value: 'submit_or_resubmit', label: 'Submit or Resubmit' },
  { value: 'send_or_reply', label: 'Send or Reply' },
  { value: 'publish_or_republish', label: 'Publish or Republish' },
];

export const CONTENT_ISSUE_OPTIONS = [
  { value: 'direct_or_off_platform_contact', label: 'Direct or Off-Platform Contact' },
  { value: 'pricing_or_price_information', label: 'Pricing or Price Information' },
  { value: 'spam_or_unwanted_promotion', label: 'Spam or Unwanted Promotion' },
  { value: 'low_quality_or_meaningless_text', label: 'Low-Quality or Meaningless Text' },
  { value: 'inappropriate_language', label: 'Inappropriate Language' },
  { value: 'abuse_or_harassment', label: 'Abuse or Harassment' },
  { value: 'hate_or_discrimination', label: 'Hate or Discrimination' },
  { value: 'threats_or_violence', label: 'Threats or Violence' },
  { value: 'sexual_or_explicit_content', label: 'Sexual or Explicit Content' },
  { value: 'scam_fraud_or_phishing', label: 'Scam, Fraud or Phishing' },
  { value: 'private_or_sensitive_information', label: 'Private or Sensitive Information' },
  { value: 'illegal_or_unsafe_content', label: 'Illegal or Unsafe Content' },
  { value: 'impersonation_or_misrepresentation', label: 'Impersonation or Misrepresentation' },
  { value: 'review_integrity_concern', label: 'Review Integrity Concern' },
  { value: 'repeated_spam_submission', label: 'Repeated Spam Submission' },
  { value: 'unsafe_image', label: 'Unsafe Image' },
  { value: 'unsafe_file_or_malware', label: 'Unsafe File or Malware' },
];

export const DETECTION_SOURCE_OPTIONS = [
  { value: 'internal', label: 'Internal Content Control' },
  { value: 'approved_integration', label: 'Approved Integration' },
  { value: 'combined', label: 'Combined' },
];

export const DETECTION_METHOD_OPTIONS = [
  { value: 'exact_text_match', label: 'Exact Text Match', role: 'primary' },
  { value: 'word_or_phrase_match', label: 'Word or Phrase Match', role: 'primary' },
  { value: 'pattern_and_context_detection', label: 'Pattern and Context Detection', role: 'primary' },
  { value: 'contact_and_link_detection', label: 'Contact and Link Detection', role: 'primary' },
  { value: 'pricing_pattern_detection', label: 'Pricing Pattern Detection', role: 'primary' },
  { value: 'protected_identity_detection', label: 'Protected Identity Detection', role: 'primary' },
  { value: 'text_safety_detection', label: 'Text Safety Detection', role: 'primary' },
  { value: 'low_quality_text_detection', label: 'Low-Quality Text Detection', role: 'primary' },
  { value: 'image_safety_detection', label: 'Image Safety Detection', role: 'primary' },
  { value: 'malware_scan', label: 'Malware Scan', role: 'primary' },
  { value: 'similarity_and_spam_repetition_detection', label: 'Similarity and Spam-Repetition Detection', role: 'primary' },
  { value: 'ocr_text_detection', label: 'OCR Text Detection', role: 'supporting' },
  { value: 'qr_code_or_barcode_detection', label: 'QR-Code or Barcode Detection', role: 'supporting' },
];

export const TEXT_BASED_METHODS = new Set([
  'exact_text_match',
  'word_or_phrase_match',
  'pattern_and_context_detection',
  'pricing_pattern_detection',
  'protected_identity_detection',
  'text_safety_detection',
  'low_quality_text_detection',
]);

export const NORMALISATION_METHODS = new Set([
  'exact_text_match',
  'word_or_phrase_match',
  'pattern_and_context_detection',
  'protected_identity_detection',
]);

export const APPROVED_PATTERN_OPTIONS = [
  { value: 'repeated_promotional_wording', label: 'Repeated promotional wording', issues: ['spam_or_unwanted_promotion', 'repeated_spam_submission'] },
  { value: 'misleading_authority_wording', label: 'Misleading authority wording', issues: ['impersonation_or_misrepresentation'] },
  { value: 'prohibited_contact_wording', label: 'Prohibited contact wording', issues: ['direct_or_off_platform_contact'] },
  { value: 'prohibited_pricing_wording', label: 'Prohibited pricing wording', issues: ['pricing_or_price_information'] },
  { value: 'review_manipulation_wording', label: 'Review manipulation wording', issues: ['review_integrity_concern'] },
  { value: 'low_quality_or_meaningless_wording', label: 'Low-quality or meaningless wording', issues: ['low_quality_or_meaningless_text'] },
  { value: 'inappropriate_language_wording', label: 'Inappropriate language wording', issues: ['inappropriate_language'] },
  { value: 'abusive_or_harassing_wording', label: 'Abusive or harassing wording', issues: ['abuse_or_harassment'] },
  { value: 'hateful_or_discriminatory_wording', label: 'Hateful or discriminatory wording', issues: ['hate_or_discrimination'] },
  { value: 'threatening_or_violent_wording', label: 'Threatening or violent wording', issues: ['threats_or_violence'] },
  { value: 'sexual_or_explicit_wording', label: 'Sexual or explicit wording', issues: ['sexual_or_explicit_content'] },
  { value: 'scam_fraud_or_phishing_wording', label: 'Scam, fraud or phishing wording', issues: ['scam_fraud_or_phishing'] },
  { value: 'private_or_sensitive_information_pattern', label: 'Private or sensitive information pattern', issues: ['private_or_sensitive_information'] },
  { value: 'illegal_or_unsafe_wording', label: 'Illegal or unsafe wording', issues: ['illegal_or_unsafe_content'] },
];

export const PROTECTED_TERM_OPTIONS = [
  { value: 'trades_poster', label: 'Trades Poster' },
  { value: 'admin', label: 'Admin' },
  { value: 'support', label: 'Support' },
  { value: 'official', label: 'Official' },
  { value: 'verified', label: 'Verified' },
  { value: 'moderator', label: 'Moderator' },
];

export const NORMALISATION_CONTROL_OPTIONS = [
  { value: 'ignore_letter_case', label: 'Ignore Letter Case' },
  { value: 'ignore_extra_spaces', label: 'Ignore Extra Spaces' },
  { value: 'ignore_punctuation', label: 'Ignore Punctuation' },
];

export const COMPARISON_DATASET_OPTIONS = [
  { value: 'same_content_owner', label: 'Same Content Owner' },
  { value: 'selected_content_area', label: 'Selected Content Area' },
];

export const LOOKBACK_UNIT_OPTIONS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

export const SPAM_CONTEXT_OPTIONS = [
  { value: 'repeated_promotional_content', label: 'Repeated promotional content', issues: ['spam_or_unwanted_promotion', 'repeated_spam_submission'] },
  { value: 'repeated_contact_or_link_sharing', label: 'Repeated contact or link sharing', issues: ['spam_or_unwanted_promotion', 'repeated_spam_submission'] },
  { value: 'repeated_pricing_content', label: 'Repeated pricing content', issues: ['spam_or_unwanted_promotion', 'repeated_spam_submission'] },
  { value: 'repeated_scam_fraud_or_phishing_content', label: 'Repeated scam, fraud or phishing content', issues: ['spam_or_unwanted_promotion', 'repeated_spam_submission'] },
  { value: 'repeated_abusive_or_disruptive_content', label: 'Repeated abusive or disruptive content', issues: ['spam_or_unwanted_promotion', 'repeated_spam_submission'] },
  { value: 'repeated_review_manipulation', label: 'Repeated review manipulation', issues: ['review_integrity_concern', 'repeated_spam_submission'] },
  { value: 'repeated_low_quality_or_meaningless_content', label: 'Repeated low-quality or meaningless content', issues: ['spam_or_unwanted_promotion', 'repeated_spam_submission'] },
];

export const MATCH_ACTION_OPTIONS = [
  { value: 'continue_current_action', label: 'Continue Current Action' },
  { value: 'show_warning', label: 'Show Warning' },
  { value: 'hold_for_review', label: 'Hold for Review' },
  { value: 'block_current_action', label: 'Block Current Action' },
];

export const REVIEW_REQUIREMENT_OPTIONS = [
  { value: 'no_review_item_required', label: 'No Review Item Required' },
  { value: 'create_secured_review_item', label: 'Create Secured Review Item' },
];

export const WARNING_RESPONSE_OPTIONS = [
  { value: 'continue_after_confirmation', label: 'Continue After Confirmation' },
  { value: 'return_to_edit', label: 'Return to Edit' },
  { value: 'cancel_current_action', label: 'Cancel Current Action' },
];

export const CONDITION_FIELD_OPTIONS = [
  { value: 'content_language', label: 'Content Language' },
  { value: 'file_type', label: 'File Type' },
];

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals', multi: false },
  { value: 'does_not_equal', label: 'Does Not Equal', multi: false },
  { value: 'is_any_of', label: 'Is Any Of', multi: true },
  { value: 'is_not_any_of', label: 'Is Not Any Of', multi: true },
];

export const FILE_TYPE_OPTIONS = [
  { value: 'image', label: 'Image' },
  { value: 'document', label: 'Document' },
  { value: 'text_file', label: 'Text File' },
  { value: 'compressed_file', label: 'Compressed File' },
  { value: 'other_supported_file', label: 'Other Supported File' },
];

export const CONTENT_LANGUAGE_OPTIONS = [
  { value: 'english', label: 'English' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'unknown', label: 'Unknown' },
];

export const DETECTION_SERVICES = [
  {
    value: 'approved_text_safety',
    label: 'Approved Text Safety Service',
    enabled: true,
    contentForms: ['text', 'image', 'attachment'],
    methods: [
      'exact_text_match',
      'word_or_phrase_match',
      'pattern_and_context_detection',
      'contact_and_link_detection',
      'text_safety_detection',
      'low_quality_text_detection',
      'similarity_and_spam_repetition_detection',
      'ocr_text_detection',
    ],
  },
  {
    value: 'approved_image_safety',
    label: 'Approved Image Safety Service',
    enabled: true,
    contentForms: ['image', 'attachment'],
    methods: [
      'image_safety_detection',
      'malware_scan',
      'ocr_text_detection',
      'qr_code_or_barcode_detection',
    ],
  },
];

const REVIEW_INTEGRITY_AREAS = new Set(['review_content', 'review_images', 'business_reply']);

const SCOPE_ROWS = [
  ['customers', 'first_name', ['account_setup'], ['signup_submit', 'save_or_update']],
  ['customers', 'last_name', ['account_setup'], ['signup_submit', 'save_or_update']],
  ['business_profiles', 'business_name', ['account_setup', 'public_content'], ['signup_submit', 'save_or_update']],
  ['business_profiles', 'profile_headline', ['account_setup', 'public_content'], ['signup_submit', 'save_or_update']],
  ['business_profiles', 'profile_description', ['account_setup', 'public_content'], ['signup_submit', 'save_or_update']],
  ['business_profiles', 'logo', ['account_setup', 'public_content'], ['upload', 'save_or_update']],
  ['business_profiles', 'header_banner', ['account_setup', 'public_content'], ['upload', 'save_or_update']],
  ['business_profiles', 'profile_image', ['account_setup', 'public_content'], ['upload', 'save_or_update']],
  ['business_profiles', 'portfolio_image', ['public_content'], ['upload', 'save_or_update']],
  ['business_profiles', 'structured_contact_details', ['structured_business_contact'], ['save_or_update']],
  ['business_profiles', 'allowed_external_links', ['structured_business_contact'], ['save_or_update']],
  ['posts', 'post_title', ['public_content'], ['submit_or_resubmit', 'publish_or_republish']],
  ['posts', 'post_description', ['public_content'], ['submit_or_resubmit', 'publish_or_republish']],
  ['posts', 'post_image', ['public_content'], ['upload', 'submit_or_resubmit', 'publish_or_republish']],
  ['messages', 'message_thread_text', ['private_communication'], ['send_or_reply']],
  ['messages', 'message_thread_image', ['private_communication'], ['upload', 'send_or_reply']],
  ['messages', 'message_thread_attachment', ['private_communication'], ['upload', 'send_or_reply']],
  ['quotes', 'quote_request_content', ['private_communication'], ['submit_or_resubmit']],
  ['quotes', 'quote_request_images', ['private_communication'], ['upload', 'submit_or_resubmit']],
  ['quotes', 'quote_request_attachments', ['private_communication'], ['upload', 'submit_or_resubmit']],
  ['quotes', 'quote_thread_text', ['private_communication'], ['send_or_reply']],
  ['quotes', 'quote_thread_image', ['private_communication'], ['upload', 'send_or_reply']],
  ['quotes', 'quote_thread_attachment', ['private_communication'], ['upload', 'send_or_reply']],
  ['reviews', 'review_content', ['public_content'], ['submit_or_resubmit']],
  ['reviews', 'review_images', ['public_content'], ['upload', 'submit_or_resubmit']],
  ['reviews', 'business_reply', ['public_content'], ['submit_or_resubmit']],
  ['support_tickets', 'ticket_subject', ['private_communication'], ['submit_or_resubmit']],
  ['support_tickets', 'ticket_description', ['private_communication'], ['submit_or_resubmit', 'send_or_reply']],
  ['support_tickets', 'ticket_evidence_attachment', ['private_evidence'], ['upload', 'submit_or_resubmit', 'send_or_reply']],
  ['appeals', 'appeal_message_text', ['private_evidence'], ['submit_or_resubmit', 'send_or_reply']],
  ['appeals', 'appeal_message_attachment', ['private_evidence'], ['upload', 'submit_or_resubmit', 'send_or_reply']],
];

/** Image/attachment areas where contact details are prohibited (require OCR + QR with contact Primary). */
const CONTACT_PROHIBITED_IMAGE_AREAS = new Set([
  'logo',
  'header_banner',
  'profile_image',
  'portfolio_image',
  'post_image',
  'message_thread_image',
  'message_thread_attachment',
  'quote_request_images',
  'quote_request_attachments',
  'quote_thread_image',
  'quote_thread_attachment',
  'review_images',
]);

export function requiresImageContactSupportingChecks(contentArea, contentIssue) {
  return contentIssue === 'direct_or_off_platform_contact' && CONTACT_PROHIBITED_IMAGE_AREAS.has(contentArea);
}

export const SCOPE_CATALOGUE = SCOPE_ROWS.map(([module, contentArea, contexts, triggers]) => ({
  module,
  contentArea,
  contexts,
  triggers,
}));

export const CHECKING_FAILURE_MESSAGE = 'We could not check this content. Please try again.';

const ALL_FORMS = ['text', 'image', 'attachment'];
const TEXT_IMAGE_ATTACHMENT = ALL_FORMS;
const IMAGE_ATTACHMENT = ['image', 'attachment'];
const TEXT_IMAGE = ['text', 'image'];

export const ISSUE_COMPATIBILITY = {
  direct_or_off_platform_contact: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['contact_and_link_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  pricing_or_price_information: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['pricing_pattern_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  spam_or_unwanted_promotion: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['similarity_and_spam_repetition_detection', 'pattern_and_context_detection', 'exact_text_match', 'word_or_phrase_match'],
  },
  low_quality_or_meaningless_text: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['low_quality_text_detection', 'pattern_and_context_detection'],
  },
  inappropriate_language: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['text_safety_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  abuse_or_harassment: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['text_safety_detection', 'image_safety_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  hate_or_discrimination: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['text_safety_detection', 'image_safety_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  threats_or_violence: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['text_safety_detection', 'image_safety_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  sexual_or_explicit_content: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['text_safety_detection', 'image_safety_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  scam_fraud_or_phishing: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['text_safety_detection', 'image_safety_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  private_or_sensitive_information: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['text_safety_detection', 'image_safety_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  illegal_or_unsafe_content: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['text_safety_detection', 'image_safety_detection', 'exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'],
  },
  impersonation_or_misrepresentation: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['protected_identity_detection', 'pattern_and_context_detection', 'exact_text_match', 'word_or_phrase_match'],
  },
  review_integrity_concern: {
    forms: TEXT_IMAGE,
    methods: ['similarity_and_spam_repetition_detection', 'pattern_and_context_detection', 'exact_text_match', 'word_or_phrase_match'],
  },
  repeated_spam_submission: {
    forms: TEXT_IMAGE_ATTACHMENT,
    methods: ['similarity_and_spam_repetition_detection', 'pattern_and_context_detection'],
  },
  unsafe_image: {
    forms: IMAGE_ATTACHMENT,
    methods: ['image_safety_detection'],
  },
  unsafe_file_or_malware: {
    forms: IMAGE_ATTACHMENT,
    methods: ['malware_scan'],
  },
};

const IMAGE_FILE_TYPES = ['image'];
const ATTACHMENT_FILE_TYPES = FILE_TYPE_OPTIONS.map((o) => o.value);
const OCR_FILE_TYPES = ['image', 'document', 'text_file'];
const QR_FILE_TYPES = ['image'];
const IMAGE_SAFETY_FILE_TYPES = ['image'];
const ALL_LANGUAGES = CONTENT_LANGUAGE_OPTIONS.map((o) => o.value);

function optionLabel(options, value) {
  return options.find((o) => o.value === value)?.label ?? value ?? '—';
}

export function getContentAreaMeta(contentArea) {
  return CONTENT_AREA_OPTIONS.find((o) => o.value === contentArea) || null;
}

export function getContentForm(contentArea) {
  return getContentAreaMeta(contentArea)?.form || '';
}

export function isPrimaryMethod(method) {
  return DETECTION_METHOD_OPTIONS.find((o) => o.value === method)?.role === 'primary';
}

export function isSupportingMethod(method) {
  return DETECTION_METHOD_OPTIONS.find((o) => o.value === method)?.role === 'supporting';
}

export function getScopeRow(module, contentArea) {
  return SCOPE_CATALOGUE.find((row) => row.module === module && row.contentArea === contentArea) || null;
}

export function getContentAreasForModule(module) {
  if (!module) return [];
  return CONTENT_AREA_OPTIONS.filter((o) => o.module === module);
}

export function getContentContextsForArea(module, contentArea) {
  const row = getScopeRow(module, contentArea);
  if (!row) return [];
  return CONTENT_CONTEXT_OPTIONS.filter((o) => row.contexts.includes(o.value));
}

export function getTriggersForScope(module, contentArea, contentContext) {
  const row = getScopeRow(module, contentArea);
  if (!row || !row.contexts.includes(contentContext)) return [];
  return TRIGGER_OPTIONS.filter((o) => row.triggers.includes(o.value));
}

export function isScopeCombinationValid(module, contentArea, contentContext, triggers = []) {
  const row = getScopeRow(module, contentArea);
  if (!row) return false;
  if (!contentContext || !row.contexts.includes(contentContext)) return false;
  if (!triggers.length) return false;
  return triggers.every((trigger) => row.triggers.includes(trigger));
}

export function getFileTypesForContentArea(contentArea) {
  const form = getContentForm(contentArea);
  if (form === 'image') return IMAGE_FILE_TYPES;
  if (form === 'attachment') return ATTACHMENT_FILE_TYPES;
  return [];
}

export function getFileTypesForMethod(method) {
  if (method === 'image_safety_detection') return IMAGE_SAFETY_FILE_TYPES;
  if (method === 'ocr_text_detection') return OCR_FILE_TYPES;
  if (method === 'qr_code_or_barcode_detection') return QR_FILE_TYPES;
  if (method === 'malware_scan') return ATTACHMENT_FILE_TYPES;
  if (TEXT_BASED_METHODS.has(method) || method === 'contact_and_link_detection' || method === 'similarity_and_spam_repetition_detection') {
    return ATTACHMENT_FILE_TYPES;
  }
  return ATTACHMENT_FILE_TYPES;
}

export function getLanguagesForMethod(method) {
  if (TEXT_BASED_METHODS.has(method) || method === 'contact_and_link_detection' || method === 'similarity_and_spam_repetition_detection') {
    return ALL_LANGUAGES;
  }
  return [];
}

export function methodSupportsOcr(contentArea) {
  const form = getContentForm(contentArea);
  return form === 'image' || form === 'attachment';
}

export function methodSupportsQr(contentArea) {
  const form = getContentForm(contentArea);
  return form === 'image' || form === 'attachment';
}

export function getCommonFileTypes(contentArea, methods = []) {
  const areaTypes = getFileTypesForContentArea(contentArea);
  if (!areaTypes.length) return [];
  return methods.reduce((common, method) => {
    const methodTypes = getFileTypesForMethod(method);
    return common.filter((type) => methodTypes.includes(type));
  }, [...areaTypes]);
}

export function getCommonLanguages(methods = []) {
  const textMethods = methods.filter((method) => getLanguagesForMethod(method).length);
  if (!textMethods.length) return [];
  return textMethods.reduce((common, method) => {
    const langs = getLanguagesForMethod(method);
    return common.filter((lang) => langs.includes(lang));
  }, [...ALL_LANGUAGES]);
}

export function languageApplies(contentArea, methods = []) {
  const form = getContentForm(contentArea);
  if (form === 'text') return true;
  return (form === 'image' || form === 'attachment') && methods.includes('ocr_text_detection');
}

export function fileTypeApplies(contentArea) {
  const form = getContentForm(contentArea);
  return form === 'image' || form === 'attachment';
}

function methodNeedsOcr(method, form) {
  if (form === 'text') return false;
  return TEXT_BASED_METHODS.has(method);
}

function methodNeedsOcrOrQr(method, form) {
  if (form === 'text') return false;
  return method === 'contact_and_link_detection';
}

export function getCompatibleDetectionMethods(contentArea, contentIssue, selectedMethods = []) {
  const form = getContentForm(contentArea);
  const compat = ISSUE_COMPATIBILITY[contentIssue];
  if (!form || !compat || !compat.forms.includes(form)) return [];

  const hasOcr = selectedMethods.includes('ocr_text_detection');
  const hasQr = selectedMethods.includes('qr_code_or_barcode_detection');
  const methods = [];

  compat.methods.forEach((method) => {
    if (method === 'image_safety_detection' && form === 'text') return;
    if (method === 'malware_scan' && form === 'text') return;
    if (methodNeedsOcr(method, form) && !hasOcr) return;
    if (methodNeedsOcrOrQr(method, form) && !hasOcr && !hasQr) return;
    if (form === 'text' && (method === 'image_safety_detection' || method === 'malware_scan')) return;
    methods.push(method);
  });

  if (methodSupportsOcr(contentArea) && compat.methods.some((method) => TEXT_BASED_METHODS.has(method) || method === 'contact_and_link_detection')) {
    methods.push('ocr_text_detection');
  }
  if (methodSupportsQr(contentArea) && (compat.methods.includes('contact_and_link_detection') || hasOcr || methods.includes('ocr_text_detection'))) {
    methods.push('qr_code_or_barcode_detection');
  }

  return [...new Set(methods)];
}

export function getContentIssuesForArea(contentArea) {
  const form = getContentForm(contentArea);
  if (!form) return [];
  return CONTENT_ISSUE_OPTIONS.filter((issue) => {
    if (issue.value === 'review_integrity_concern' && !REVIEW_INTEGRITY_AREAS.has(contentArea)) return false;
    const compat = ISSUE_COMPATIBILITY[issue.value];
    return Boolean(compat?.forms.includes(form) && compat.methods.length);
  });
}

export function getDetectionService(value) {
  return DETECTION_SERVICES.find((service) => service.value === value) || null;
}

export function getMethodSource(method, detectionSource, detectionService) {
  const internal = DETECTION_METHOD_OPTIONS.some((o) => o.value === method);
  const service = getDetectionService(detectionService);
  const integration = Boolean(service?.methods.includes(method));
  if (detectionSource === 'internal') return internal ? 'internal' : null;
  if (detectionSource === 'approved_integration') return integration ? 'integration' : null;
  if (internal && integration) return 'both';
  if (internal) return 'internal';
  if (integration) return 'integration';
  return null;
}

export function getMethodSourceLabel(source) {
  if (source === 'internal') return 'Internal';
  if (source === 'integration') return 'Integration';
  if (source === 'both') return 'Both';
  return '';
}

export function getAvailableDetectionMethods(form) {
  const compatible = getCompatibleDetectionMethods(form.contentArea, form.contentIssue, form.detectionMethods);
  const source = form.detectionSource;
  const service = form.detectionService;
  return compatible.filter((method) => {
    if (source === 'internal') return true;
    if (source === 'approved_integration') return getMethodSource(method, source, service) === 'integration';
    return Boolean(getMethodSource(method, source, service));
  });
}

export function getDetectionServiceOptions(form) {
  if (!CCT_APPROVED_SERVICES_CONFIRMED) return [];
  const contentForm = getContentForm(form.contentArea);
  const selected = form.detectionMethods || [];
  return DETECTION_SERVICES.filter((service) => {
    if (!service.enabled) return false;
    if (contentForm && !service.contentForms.includes(contentForm)) return false;
    const integrationMethods = selected.filter((method) => {
      const methodSource = getMethodSource(method, form.detectionSource || 'combined', service.value);
      return methodSource === 'integration' || methodSource === 'both';
    });
    if (!integrationMethods.length) return true;
    return integrationMethods.every((method) => service.methods.includes(method));
  }).map((service) => ({ value: service.value, label: service.label }));
}

export function getApprovedPatternsForIssue(contentIssue) {
  return APPROVED_PATTERN_OPTIONS.filter((o) => o.issues.includes(contentIssue));
}

export function getSpamContextsForIssue(contentIssue) {
  return SPAM_CONTEXT_OPTIONS.filter((o) => o.issues.includes(contentIssue));
}

export function operatorIsMulti(operator) {
  return CONDITION_OPERATOR_OPTIONS.find((o) => o.value === operator)?.multi === true;
}

export function getModuleLabel(value) {
  return optionLabel(MODULE_OPTIONS, value);
}

export function getContentAreaLabel(value) {
  return optionLabel(CONTENT_AREA_OPTIONS, value);
}

export function getContentContextLabel(value) {
  return optionLabel(CONTENT_CONTEXT_OPTIONS, value);
}

export function getTriggerLabel(value) {
  return optionLabel(TRIGGER_OPTIONS, value);
}

export function getContentIssueLabel(value) {
  return optionLabel(CONTENT_ISSUE_OPTIONS, value);
}

export function getDetectionSourceLabel(value) {
  return optionLabel(DETECTION_SOURCE_OPTIONS, value);
}

export function getDetectionServiceLabel(value) {
  return optionLabel(DETECTION_SERVICES, value);
}

export function getDetectionMethodLabel(value) {
  return optionLabel(DETECTION_METHOD_OPTIONS, value);
}

export function getApprovedPatternLabel(value) {
  return optionLabel(APPROVED_PATTERN_OPTIONS, value);
}

export function getProtectedTermLabel(value) {
  return optionLabel(PROTECTED_TERM_OPTIONS, value);
}

export function getNormalisationLabel(value) {
  return optionLabel(NORMALISATION_CONTROL_OPTIONS, value);
}

export function getComparisonDatasetLabel(value) {
  return optionLabel(COMPARISON_DATASET_OPTIONS, value);
}

export function getLookbackUnitLabel(value) {
  return optionLabel(LOOKBACK_UNIT_OPTIONS, value);
}

export function getSpamContextLabel(value) {
  return optionLabel(SPAM_CONTEXT_OPTIONS, value);
}

export function getMatchActionLabel(value) {
  return optionLabel(MATCH_ACTION_OPTIONS, value);
}

export function getReviewRequirementLabel(value) {
  return optionLabel(REVIEW_REQUIREMENT_OPTIONS, value);
}

export function getWarningResponseLabel(value) {
  return optionLabel(WARNING_RESPONSE_OPTIONS, value);
}

export function getConditionFieldLabel(value) {
  return optionLabel(CONDITION_FIELD_OPTIONS, value);
}

export function getConditionOperatorLabel(value) {
  return optionLabel(CONDITION_OPERATOR_OPTIONS, value);
}

export function getFileTypeLabel(value) {
  return optionLabel(FILE_TYPE_OPTIONS, value);
}

export function getContentLanguageLabel(value) {
  return optionLabel(CONTENT_LANGUAGE_OPTIONS, value);
}

export function getConditionValueLabel(field, value) {
  if (field === 'file_type') return getFileTypeLabel(value);
  if (field === 'content_language') return getContentLanguageLabel(value);
  return value || '—';
}

export function formatTriggerList(triggers = []) {
  if (!triggers.length) return '—';
  return triggers.map(getTriggerLabel).join(' · ');
}

export function formatDetectionMethodList(methods = []) {
  if (!methods.length) return '—';
  return methods.map(getDetectionMethodLabel).join(' · ');
}

export function formatLookbackPeriod(value, unit) {
  if (!value || !unit) return '—';
  return `${value} ${getLookbackUnitLabel(unit).toLowerCase()}`;
}

export function formatMatchOutcome(action, reviewRequirement) {
  if (action === 'block_current_action') {
    return reviewRequirement === 'create_secured_review_item' ? 'Block + Review' : 'Block';
  }
  // Legacy retained values for older saved rows
  if (action === 'show_warning') return 'Warning';
  if (action === 'hold_for_review') return 'Hold + Review';
  if (action === 'continue_current_action') {
    return reviewRequirement === 'create_secured_review_item' ? 'Continue + Review' : 'Continue';
  }
  return 'Block';
}

export function formatOutcomes(rule) {
  return `${formatMatchOutcome(rule.confirmedAction, rule.confirmedReviewRequirement)} / ${formatMatchOutcome(rule.suspectedAction, rule.suspectedReviewRequirement)}`;
}

export function formatConditionLine(condition) {
  const field = getConditionFieldLabel(condition.field);
  const operator = getConditionOperatorLabel(condition.operator);
  const values = (condition.values || []).map((value) => getConditionValueLabel(condition.field, value));
  return `${field} · ${operator} · ${values.join(', ')}`;
}

export function formatConditionsPreview(conditions = []) {
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map(formatConditionLine);
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return {
    text: `${lines[0]} · +${lines.length - 1} more`,
    extraCount: lines.length - 1,
    lines,
  };
}

export function formatContentControlDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${suffix}`;
}

export function nextContentControlRuleId(rules) {
  const nums = (rules || []).map((rule) => {
    const match = String(rule.id || '').match(/^CCT-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `CCT-${String(max + 1).padStart(4, '0')}`;
}

export function createEmptyContentControlForm() {
  return {
    id: '',
    ruleName: '',
    description: '',
    module: '',
    contentArea: '',
    contentContext: '',
    triggers: [],
    contentIssue: '',
    detectionSource: 'internal',
    detectionService: '',
    detectionMethods: [],
    exactTextValues: [],
    wordOrPhraseValues: [],
    approvedPatterns: [],
    protectedTerms: [],
    normalisationControls: [],
    comparisonDataset: '',
    similarityThreshold: '',
    repetitionCount: '',
    lookbackPeriodValue: '',
    lookbackPeriodUnit: '',
    spamContext: '',
    conditions: [],
    confirmedAction: 'block_current_action',
    confirmedReviewRequirement: 'no_review_item_required',
    suspectedAction: 'block_current_action',
    suspectedReviewRequirement: 'no_review_item_required',
    noMatchBehaviour: 'continue_current_action',
    blockMessage: '',
    holdMessage: '',
    warningTitle: '',
    warningMessage: '',
    warningResponse: '',
    changeReason: '',
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

function uniqueSorted(values = []) {
  return [...new Set(values.filter(Boolean))].sort();
}

export function normalizeContentControlForm(raw) {
  if (!raw) return createEmptyContentControlForm();
  const base = createEmptyContentControlForm();
  return {
    ...base,
    ...raw,
    ruleName: raw.ruleName ?? '',
    description: raw.description ?? '',
    module: raw.module || '',
    contentArea: raw.contentArea || raw.surface || '',
    contentContext: raw.contentContext || '',
    triggers: uniqueSorted(raw.triggers || []),
    contentIssue: raw.contentIssue || raw.breachCategory || '',
    detectionSource: raw.detectionSource || 'internal',
    detectionService: raw.detectionService || '',
    detectionMethods: uniqueSorted(raw.detectionMethods || (raw.checkMethod ? [raw.checkMethod] : [])),
    exactTextValues: Array.isArray(raw.exactTextValues) ? raw.exactTextValues : [],
    wordOrPhraseValues: Array.isArray(raw.wordOrPhraseValues) ? raw.wordOrPhraseValues : [],
    approvedPatterns: uniqueSorted(raw.approvedPatterns || []),
    protectedTerms: uniqueSorted(raw.protectedTerms || []),
    normalisationControls: uniqueSorted(raw.normalisationControls || []),
    conditions: Array.isArray(raw.conditions) ? raw.conditions : [],
    // Live responses are fixed Block / Continue. Flags are owned by Configuration — Flag.
    confirmedAction: 'block_current_action',
    confirmedReviewRequirement: 'no_review_item_required',
    suspectedAction: 'block_current_action',
    suspectedReviewRequirement: 'no_review_item_required',
    noMatchBehaviour: 'continue_current_action',
    blockMessage: raw.blockMessage || '',
    holdMessage: '',
    warningTitle: '',
    warningMessage: '',
    warningResponse: '',
    changeReason: raw.changeReason || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
  };
}

export function matchesContentControlSearch(rule, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    rule.id,
    rule.ruleName,
    getModuleLabel(rule.module),
    getContentAreaLabel(rule.contentArea),
    getContentContextLabel(rule.contentContext),
    getContentIssueLabel(rule.contentIssue),
    ...(rule.triggers || []).map(getTriggerLabel),
    ...(rule.detectionMethods || []).map(getDetectionMethodLabel),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function matchesContentControlFilters(rule, filters) {
  if (filters.module && filters.module !== 'all' && rule.module !== filters.module) return false;
  if (filters.contentArea && filters.contentArea !== 'all' && rule.contentArea !== filters.contentArea) return false;
  if (filters.contentContext && filters.contentContext !== 'all' && rule.contentContext !== filters.contentContext) return false;
  if (filters.contentIssue && filters.contentIssue !== 'all' && rule.contentIssue !== filters.contentIssue) return false;
  return true;
}

export const CCT_FILTER_ALL = 'all';

export const CCT_MODULE_FILTER_OPTIONS = [
  { value: CCT_FILTER_ALL, label: 'All Modules' },
  ...MODULE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

export const CCT_CONTENT_AREA_FILTER_OPTIONS = [
  { value: CCT_FILTER_ALL, label: 'All Content Areas' },
  ...CONTENT_AREA_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

export const CCT_CONTENT_CONTEXT_FILTER_OPTIONS = [
  { value: CCT_FILTER_ALL, label: 'All Content Contexts' },
  ...CONTENT_CONTEXT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

export const CCT_CONTENT_ISSUE_FILTER_OPTIONS = [
  { value: CCT_FILTER_ALL, label: 'All Content Issues' },
  ...CONTENT_ISSUE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

export function sortContentControlRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    return bTime - aTime;
  });
}

function actorFields(lastUpdated) {
  return {
    addedBy: 'Olivia Brown',
    lastUpdatedBy: 'Olivia Brown',
    dateAdded: lastUpdated,
    lastUpdated,
  };
}

export function buildSampleContentControlRules() {
  const samples = [
    {
      id: 'CCT-0001',
      ruleName: 'Check Message Language',
      description: 'Blocks inappropriate language in private message thread text.',
      module: 'messages',
      contentArea: 'message_thread_text',
      contentContext: 'private_communication',
      triggers: ['send_or_reply'],
      contentIssue: 'inappropriate_language',
      detectionSource: 'internal',
      detectionMethods: ['text_safety_detection'],
      blockMessage: 'This message cannot be sent because it includes inappropriate language.',
      changeReason: 'Added language checks for private message threads.',
      ...actorFields('2026-08-10T09:20:00+10:00'),
    },
    {
      id: 'CCT-0002',
      ruleName: 'Check Post Text Quality',
      description: 'Blocks low-quality or meaningless text in public post descriptions.',
      module: 'posts',
      contentArea: 'post_description',
      contentContext: 'public_content',
      triggers: ['submit_or_resubmit', 'publish_or_republish'],
      contentIssue: 'low_quality_or_meaningless_text',
      detectionSource: 'internal',
      detectionMethods: ['low_quality_text_detection'],
      blockMessage: 'This post cannot continue because the description is low-quality or meaningless.',
      changeReason: 'Added text-quality checks for public post descriptions.',
      ...actorFields('2026-08-10T09:35:00+10:00'),
    },
    {
      id: 'CCT-0003',
      ruleName: 'Protect Platform Identity',
      description: 'Blocks business names that impersonate platform identity terms.',
      module: 'business_profiles',
      contentArea: 'business_name',
      contentContext: 'account_setup',
      triggers: ['signup_submit', 'save_or_update'],
      contentIssue: 'impersonation_or_misrepresentation',
      detectionSource: 'internal',
      detectionMethods: ['protected_identity_detection'],
      protectedTerms: ['trades_poster', 'admin', 'support', 'official', 'verified', 'moderator'],
      normalisationControls: ['ignore_letter_case', 'ignore_extra_spaces', 'ignore_punctuation'],
      blockMessage: 'This business name cannot be used because it impersonates platform identity.',
      changeReason: 'Added protected identity checks for business names.',
      ...actorFields('2026-08-10T10:05:00+10:00'),
    },
    {
      id: 'CCT-0004',
      ruleName: 'Check Language in Review Images',
      description: 'Blocks inappropriate language found in public review images.',
      module: 'reviews',
      contentArea: 'review_images',
      contentContext: 'public_content',
      triggers: ['upload', 'submit_or_resubmit'],
      contentIssue: 'inappropriate_language',
      detectionSource: 'internal',
      detectionMethods: ['ocr_text_detection', 'text_safety_detection'],
      blockMessage: 'This review image cannot be submitted because it includes inappropriate language.',
      changeReason: 'Added language checks for review images.',
      ...actorFields('2026-08-10T10:25:00+10:00'),
    },
    {
      id: 'CCT-0005',
      ruleName: 'Block Malware in Ticket Evidence',
      description: 'Blocks malware in support ticket evidence attachments.',
      module: 'support_tickets',
      contentArea: 'ticket_evidence_attachment',
      contentContext: 'private_evidence',
      triggers: ['upload', 'submit_or_resubmit', 'send_or_reply'],
      contentIssue: 'unsafe_file_or_malware',
      detectionSource: 'internal',
      detectionMethods: ['malware_scan'],
      blockMessage: 'This file cannot be uploaded because it did not pass the malware scan.',
      changeReason: 'Added malware scanning for ticket evidence attachments.',
      ...actorFields('2026-08-10T10:40:00+10:00'),
    },
    {
      id: 'CCT-0006',
      ruleName: 'Check Profile Headline Quality',
      description: 'Blocks low-quality or meaningless business profile headlines.',
      module: 'business_profiles',
      contentArea: 'profile_headline',
      contentContext: 'public_content',
      triggers: ['save_or_update'],
      contentIssue: 'low_quality_or_meaningless_text',
      detectionSource: 'internal',
      detectionMethods: ['low_quality_text_detection'],
      blockMessage: 'This headline cannot be saved because it is low-quality or meaningless.',
      changeReason: 'Added text-quality checks for profile headlines.',
      ...actorFields('2026-08-10T11:00:00+10:00'),
    },
  ];

  return samples.map((rule) => normalizeContentControlForm(rule));
}

/** @deprecated — compatibility aliases for test simulation until that module is updated */
export const CCT_MODULE_OPTIONS = MODULE_OPTIONS;
export const SURFACE_OPTIONS_BY_MODULE = MODULE_OPTIONS.reduce((acc, module) => {
  acc[module.value] = getContentAreasForModule(module.value).map((area) => ({
    value: area.value,
    label: area.label,
  }));
  return acc;
}, {});
export const BREACH_CATEGORY_OPTIONS = CONTENT_ISSUE_OPTIONS;
export const CHECK_METHOD_OPTIONS = DETECTION_METHOD_OPTIONS.map(({ value, label }) => ({ value, label }));
export const PRIMARY_ACTION_OPTIONS = MATCH_ACTION_OPTIONS;
export const WARNING_BEHAVIOUR_OPTIONS = WARNING_RESPONSE_OPTIONS;
export const getSurfaceLabel = getContentAreaLabel;
export const getBreachCategoryLabel = getContentIssueLabel;
export const getCheckMethodLabel = getDetectionMethodLabel;
export const getPrimaryActionLabel = getMatchActionLabel;
export const getWarningBehaviourLabel = getWarningResponseLabel;
export function getSurfacesForModule(moduleValue) {
  return getContentAreasForModule(moduleValue);
}

export function getBreachCategoriesForScope(_module, surface) {
  return getContentIssuesForArea(surface);
}

export function getCheckMethodsForScope(breachCategory, surface) {
  return getCompatibleDetectionMethods(surface, breachCategory, []);
}

export function getPrimaryActionsForSurface() {
  return MATCH_ACTION_OPTIONS;
}

export function filterCheckMethodOptions(methodValues = []) {
  return DETECTION_METHOD_OPTIONS.filter((option) => methodValues.includes(option.value)).map(({ value, label }) => ({
    value,
    label,
  }));
}

export function isCheckInputRequiredForMethod(method) {
  return [
    'exact_text_match',
    'word_or_phrase_match',
    'pattern_and_context_detection',
    'protected_identity_detection',
    'similarity_and_spam_repetition_detection',
  ].includes(method);
}
